/**
 * Removes text inside parentheses () from a string. Used for display formatting.
 * e.g. "North Block (Ph-I), New Delhi" -> "North Block , New Delhi"
 */
export function stripBrackets(text) {
  if (text == null || typeof text !== 'string') return text ?? '';
  return text.replace(/\s*\([^)]*\)/g, '').replace(/\s{2,}/g, ' ').trim();
}

/**
 * Indian date format (DD-MM-YYYY) for display throughout the application.
 * Accepts: YYYY-MM-DD string, Date object, or null/undefined.
 * Returns: DD-MM-YYYY string for display, or fallback for invalid/empty values.
 */
export function formatDateIN(value, fallback = '-') {
  if (value == null || value === '') return fallback;
  let d;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    // Support YYYY-MM-DD and ISO strings (e.g. 2025-03-05T00:00:00.000Z)
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const datePart = trimmed.slice(0, 10);
      const [y, m, day] = datePart.split('-');
      if (y && m && day) d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(day, 10));
      else return fallback;
    } else if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(trimmed)) {
      const [p1, p2, p3] = trimmed.split(/[-/]/);
      const day = parseInt(p1, 10);
      const month = parseInt(p2, 10);
      const year = parseInt(p3, 10);
      d = new Date(year, month - 1, day);
      if (Number.isNaN(d.getTime())) return fallback;
    } else {
      d = new Date(trimmed);
    }
  } else {
    return fallback;
  }
  if (Number.isNaN(d.getTime())) return fallback;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

/**
 * Returns YYYY-MM-DD for form inputs and API. Use for initializing date inputs.
 */
export function getTodayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Project progress: % of construction period elapsed as of today (date-only, local).
 * Uses start and end dates; returns 0–100. Used for progress bars across Overview and Project Scope.
 * @param {string|Date|null} startDate - Project start
 * @param {string|Date|null} endDate - Project end
 * @returns {number} 0–100
 */
export function getProjectElapsedPercent(startDate, endDate) {
  if (startDate == null || endDate == null) return 0;
  const toLocal = (v) => {
    if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate()).getTime();
    if (typeof v === 'string') {
      const part = v.trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
        const [y, m, d] = part.split('-').map(Number);
        return new Date(y, m - 1, d).getTime();
      }
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    }
    return null;
  };
  const start = toLocal(startDate);
  const end = toLocal(endDate);
  if (start == null || end == null || end <= start) return 0;
  const today = new Date();
  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const elapsedMs = Math.max(0, todayLocal - start);
  const totalMs = end - start;
  const pct = Math.round((elapsedMs / totalMs) * 100);
  return Math.min(100, Math.max(0, pct));
}

/**
 * Total construction days between start and end (date-only, local).
 */
export function getProjectConstructionDays(startDate, endDate) {
  if (startDate == null || endDate == null) return null;
  const toLocal = (v) => {
    if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate()).getTime();
    if (typeof v === 'string') {
      const part = v.trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(part)) {
        const [y, m, d] = part.split('-').map(Number);
        return new Date(y, m - 1, d).getTime();
      }
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    }
    return null;
  };
  const start = toLocal(startDate);
  const end = toLocal(endDate);
  if (start == null || end == null) return null;
  return Math.round((end - start) / (1000 * 60 * 60 * 24));
}

/**
 * Indian date-time format (DD-MM-YYYY, hh:mm am/pm) for timestamps.
 */
export function formatDateTimeIN(value, fallback = '-') {
  if (value == null || value === '') return fallback;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  const mmStr = String(m).padStart(2, '0');
  return `${dd}-${mm}-${yyyy}, ${h12}:${mmStr} ${ampm}`;
}
